import React from 'react'
import { Grid, Heading, Show, MetaList, PageSection, VerticalSpacer, Hide } from '@superhi/design'
import TopicTags from '@superhi/design/dist/cjs/components/library/TopicTags/TopicTags'
import { formatDateOnly } from '@superhi/design/dist/cjs/components/utils/DateTime'

import LibraryPostVideoCard from '../../../../components/LibraryPostVideoCard'
import LibraryPostArticleCard from '../../../../components/LibraryPostArticleCard'
import Page from '../../../../components/Page'
import MarketingBlockMembership from '../../../../components/MembershipProductCallout'

import SeriesList from './components/SeriesList'
import SeriesSection from './components/Series'
import NewsletterSignup from './components/NewsletterSignup'
import ArticleIntro from './components/ArticleIntro'
import RelatedPosts from './components/RelatedPosts'
import { GridCellDesktop } from './styles'
import GuideInfo from './components/GuideInfo'

const GRID_1_CELL_SPAN_1: React.ComponentProps<typeof Grid.Cell>['span'] = {
  small: 'full',
  medium: 7,
  large: 8,
  xlarge: 7,
}
const VS_1 = { small: 24, medium: 32, large: 40 }
const GRID_1_CELL_SPAN_2: React.ComponentProps<typeof Grid.Cell>['span'] = {
  small: 'full',
  medium: 5,
  large: 4,
  xlarge: [9, 12],
}

type LibraryPost =
  | React.ComponentProps<typeof LibraryPostVideoCard>
  | React.ComponentProps<typeof LibraryPostArticleCard>

type Props = {
  type: 'article' | 'video'
  slug: string
  title: string
  intro: string
  metaItems?: React.ComponentProps<typeof MetaList>['items']
  header: React.ReactElement
  sideBar?: React.ReactElement
  guide?: {
    slug: string
    title: string
    posts: { id: string; slug: string }[]
  }
  series?: {
    slug: string
    posts: LibraryPost[] | null
  }
  relatedPosts: LibraryPost[]
  description: string
  imageUrl: string
  publishedAt: string
  topics: React.ComponentProps<typeof TopicTags>['children']
}

const LibraryPostBase: React.FC<Props> = ({
  type,
  slug,
  title,
  intro,
  metaItems = [],
  children,
  sideBar,
  guide,
  header,
  series,
  relatedPosts = [],
  topics,
  description,
  imageUrl,
  publishedAt,
}) => {
  const metaItemsFinal = React.useMemo(
    () => [
      {
        title: 'Published',
        content: formatDateOnly(publishedAt),
      },
      ...metaItems,
      {
        title: 'Topics',
        content: <TopicTags>{topics}</TopicTags>,
        fullWidth: true,
      },
    ],
    [metaItems, topics, publishedAt],
  )

  return (
    <Page title={title} description={description} imageUrl={imageUrl} imageFm="png8">
      {header}

      {guide && (
        <Show at={['small', 'medium']}>
          <GuideInfo {...guide} currentPostSlug={slug} />
        </Show>
      )}

      <PageSection>
        <Grid>
          <Grid.Cell span={GRID_1_CELL_SPAN_1}>
            <VerticalSpacer amount={VS_1}>
              <Heading level="1">{title}</Heading>

              {metaItemsFinal.length > 0 && (
                <Show at={['small', 'medium']}>
                  <MetaList items={metaItemsFinal} />
                </Show>
              )}

              <ArticleIntro>{intro}</ArticleIntro>

              {children}
            </VerticalSpacer>
          </Grid.Cell>

          <GridCellDesktop span={GRID_1_CELL_SPAN_2}>
            <VerticalSpacer amount={{ small: 24, medium: 24, large: 40 }}>
              {guide && (
                <Hide at={['small', 'medium']}>
                  <GuideInfo {...guide} currentPostSlug={slug} />
                </Hide>
              )}

              {metaItemsFinal.length > 0 && (
                <div>
                  <MetaList items={metaItemsFinal} />
                </div>
              )}

              {sideBar}

              {(series?.posts?.length || 0) > 0 && (
                <SeriesList
                  slug={series!.slug}
                  currentPostSlug={slug}
                  posts={
                    series!.posts?.map((seriesPost) => ({
                      title: seriesPost.title,
                      slug: seriesPost.slug,
                    })) ?? []
                  }
                />
              )}
            </VerticalSpacer>
          </GridCellDesktop>
        </Grid>
      </PageSection>

      {/* <MarketingBlockMembership noTopPadding /> */}

      {(series?.posts?.length || 0) > 0 && (
        <SeriesSection slug={series!.slug} currentPostSlug={slug} posts={series!.posts ?? []} />
      )}

      {relatedPosts.length > 0 && (
        <RelatedPosts
          title={type === 'article' ? 'Related posts' : 'More videos'}
          backgroundColor={type === 'article' ? 'yellow' : 'blue'}
          posts={relatedPosts}
        />
      )}

      <NewsletterSignup />
    </Page>
  )
}

export default LibraryPostBase
